import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import MarketingFooter from '../components/MarketingFooter';
import { Reveal, RevealGroup } from '../components/ui/Reveal';
import { LeadershipPreview } from '../components/team/LeadershipPreview';
import { TEAM_PAGE_LIVE } from '../config/siteFlags';
import './AboutPage.css';

const AZURE = '#0076A3';

/* ── Count-up (IntersectionObserver + rAF, reduced-motion aware) ── */
function CountUp({ to, suffix = '', duration = 1600 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setVal(to); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !done.current) {
          done.current = true;
          const start = performance.now();
          const step = (now) => {
            const p = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(eased * to));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
}

const stroke = (paths) => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths}</svg>
);

const Tick = (
  <svg className="about-tick" width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const Arrow = (
  <svg className="about-flow-arrow" width="22" height="22" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);

const PROBLEMS = [
  'Too many resumes, too little quality',
  "Job descriptions don't always capture what the hiring manager really wants",
  'Recruiters spend too much time screening',
  "Resume experience doesn't necessarily equal actual capability",
  'Interview decisions are inconsistent',
  'Candidates are assessed differently by different interviewers',
  'Hiring managers lose confidence in recruitment',
  'Background verification takes too long',
  'Candidates can cheat or plagiarise',
  'The right candidate can still be missed',
];

const EVOLUTION = [
  {
    tag: 'The question', year: '2023', name: 'ConsultIQ',
    q: 'Can we compare a Job Description with a Resume and determine how well they actually match?',
    desc: 'JD vs. Resume scoring and ranking — the first attempt to put a number on fit.',
    chips: ['JD', 'Resume', 'Match score'],
  },
  {
    tag: 'Adding intelligence', year: '2024', name: 'RecruitIQ',
    q: 'What if we could add intelligence to that matching?',
    desc: 'Move beyond keywords and basic matching into relevance, skills, experience and role requirements.',
    chips: ['Relevance', 'Skills', 'Experience', 'Requirements'],
  },
  {
    tag: 'Beyond the resume', year: '2025', name: 'SkillIQ',
    q: 'A resume tells you where someone has been. It doesn\u2019t necessarily tell you what they can actually do.',
    desc: 'Capability became measurable — assessments, interviews and verification.',
    chips: ['Resume\u2013JD', 'Skill assessment', 'Interviews', 'BGV'],
  },
  {
    tag: 'One connected engine', year: '2026', name: 'CHIRE', chire: true,
    q: 'What if all this intelligence could become part of one connected hiring and talent ecosystem?',
    desc: 'ConsultMeIn Hiring Intelligence, Recruitment Engine.',
    chips: ['Hiring intelligence', 'Recruitment engine', 'Psychometrics'],
  },
];

const CAPABILITIES = [
  { t: 'Resume & JD Intelligence', d: "Understand how closely a candidate's experience and skills align with the actual requirements of a role.",
    icon: stroke(<><path d="M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8" /><polyline points="13 3 13 8 18 8" /><path d="M13 3l6 6" /><circle cx="17.5" cy="17.5" r="3.2" /><line x1="21.5" y1="21.5" x2="19.8" y2="19.8" /></>) },
  { t: 'Skill & Talent Assessment', d: 'Go beyond the resume and understand capability.',
    icon: stroke(<><line x1="5" y1="21" x2="5" y2="12" /><line x1="12" y1="21" x2="12" y2="7" /><line x1="19" y1="21" x2="19" y2="14" /></>) },
  { t: 'Intelligent Interviews', d: 'Bring greater consistency, structure and intelligence into candidate evaluation.',
    icon: stroke(<><rect x="2" y="4" width="14" height="13" rx="2" /><polygon points="22 6 16 10 22 14 22 6" transform="translate(0 -0.5)" /><line x1="6" y1="20" x2="12" y2="20" /></>) },
  { t: 'Psychometric Assessment', d: 'Understand behavioural and psychological dimensions that can help organizations evaluate team and role alignment.',
    icon: stroke(<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.4" fill="currentColor" /></>) },
  { t: 'Background Verification', d: 'Build greater confidence in candidate information and history.',
    icon: stroke(<><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z" /><polyline points="9.2 12 11.2 14 15 10" /></>) },
  { t: 'Secure Talent Intelligence', d: 'Protect and preserve the intelligence created throughout the hiring process.',
    icon: stroke(<><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /><circle cx="12" cy="15" r="1.4" /></>) },
  { t: 'AI-Powered HR Intelligence', d: 'Create an operating layer that can eventually extend beyond hiring into the broader talent lifecycle.',
    icon: stroke(<><circle cx="6" cy="6" r="2.2" /><circle cx="18" cy="7" r="2.2" /><circle cx="7" cy="18" r="2.2" /><circle cx="17" cy="17" r="2.2" /><line x1="8" y1="7" x2="16" y2="7" /><line x1="7" y1="8" x2="7" y2="16" /><line x1="8.5" y1="16.5" x2="15.5" y2="8" /><line x1="9" y1="18" x2="15" y2="17.5" /></>) },
];

const TRUST = [
  { t: 'Accuracy', d: 'Better information creates better signals.' },
  { t: 'Fairness', d: 'People should be evaluated on capability rather than assumptions.' },
  { t: 'Transparency', d: 'Important decisions should not disappear inside a black box.' },
  { t: 'Human Oversight', d: 'AI should augment human judgment, not eliminate it.' },
  { t: 'Data Security', d: 'Talent information deserves the highest level of care.' },
  { t: 'Explainable AI', d: 'Intelligence should help people understand the why, not simply provide an answer.' },
  { t: 'Enterprise Reliability', d: 'Technology supporting people decisions must be dependable, secure and scalable.' },
];

const JOURNEY = [
  { y: '2021', t: 'ConsultMeIn begins' },
  { t: 'Leadership Hiring' },
  { t: 'Executive Hiring' },
  { t: 'Executive Search' },
  { t: '50+ CHRO conversations' },
  { t: '100+ Recruitment Head conversations' },
  { t: 'ConsultIQ' },
  { t: 'RecruitIQ' },
  { t: 'SkillIQ' },
  { t: 'CHIRE', chire: true },
];

const LIFECYCLE = [
  { t: 'Discover', today: true }, { t: 'Match', today: true }, { t: 'Assess', today: true },
  { t: 'Interview', today: true }, { t: 'Verify', today: true }, { t: 'Hire', today: true },
  { t: 'Develop', future: true }, { t: 'Grow', future: true },
];

const FUTURE_AREAS = ['Internal Hiring', 'Internal Mobility', 'Skills Intelligence', 'Talent Development',
  'Career Growth', 'Workforce Intelligence', 'Broader HR Intelligence'];

function HeroVisual() {
  return (
    <div className="about-hero-visual" aria-hidden="true">
      <svg viewBox="0 0 440 400" width="100%" role="presentation"
        style={{ display: 'block', maxWidth: 480, height: 'auto', margin: '0 auto', overflow: 'visible' }}>
        <defs>
          <linearGradient id="ag" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00A0E0" /><stop offset="100%" stopColor="#F05000" />
          </linearGradient>
          <radialGradient id="agGlow" cx="50%" cy="42%" r="60%">
            <stop offset="0%" stopColor="#D8F0FA" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#D8F0FA" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="220" cy="175" r="170" fill="url(#agGlow)" />
        {/* connecting network lines */}
        <g stroke="#9FD4E8" strokeWidth="1.4" opacity="0.8">
          <line x1="70" y1="90" x2="220" y2="175" /><line x1="360" y1="80" x2="220" y2="175" />
          <line x1="60" y1="250" x2="220" y2="175" /><line x1="370" y1="260" x2="220" y2="175" />
          <line x1="150" y1="320" x2="220" y2="175" /><line x1="300" y1="330" x2="220" y2="175" />
        </g>
        <g className="about-dash" stroke="url(#ag)" strokeWidth="2" fill="none" opacity="0.7">
          <path d="M70 90 L220 175 L370 260" /><path d="M60 250 L220 175 L360 80" />
        </g>
        {/* satellite talent nodes */}
        {[['70','90','#00A0E0'],['360','80','#F05000'],['60','250','#0076A3'],
          ['370','260','#00A0E0'],['150','320','#F05000'],['300','330','#0076A3']].map(([x, y, c], i) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="9" fill="#fff" stroke={c} strokeWidth="3"
            className={i % 2 ? 'about-float-2' : 'about-float'} />
        ))}
        {/* central intelligence core */}
        <circle cx="220" cy="175" r="52" fill="#fff" stroke="url(#ag)" strokeWidth="3" />
        <circle cx="220" cy="175" r="52" fill="none" stroke="#F05000" strokeWidth="1" opacity="0.4" className="about-pulse" />
        <text x="220" y="172" textAnchor="middle" fontFamily="Cabinet Grotesk, sans-serif"
          fontWeight="800" fontSize="22" fill="#002A38">CHIRE</text>
        <text x="220" y="192" textAnchor="middle" fontFamily="JetBrains Mono, monospace"
          fontSize="8" letterSpacing="1.5" fill="#5B6B74">INTELLIGENCE</text>
        {/* abstract human silhouette within a node */}
        <g transform="translate(360 80)" fill="#F05000">
          <circle cx="0" cy="-3" r="3.2" /><path d="M-5 6c0-3 2.4-5 5-5s5 2 5 5z" />
        </g>
      </svg>
    </div>
  );
}

export default function AboutPage() {
  const navigate = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="about-page" style={{ background: '#F5FAFC', minHeight: '100vh', overflowX: 'hidden' }} data-testid="about-page">
      <PublicNav />

      {/* ─── HERO ─── */}
      <section className="about-section" style={{ paddingBottom: 'clamp(2rem,4vw,3rem)' }}>
        <div className="about-container about-hero">
          <div>
            <Reveal now>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.9rem', background: '#FFEADD', border: '1px solid #F0500033', borderRadius: 999, marginBottom: '1.5rem', fontSize: '0.75rem', fontWeight: 600, color: '#D84400', letterSpacing: '0.04em' }}>
                <span style={{ width: 7, height: 7, borderRadius: 999, background: '#F05000' }} /> Our story · Since 2021
              </div>
            </Reveal>
            <Reveal now delay={0.08}>
              <h1 className="about-h1">It Didn't Start With Artificial Intelligence.<br /><span style={{ color: AZURE }}>It Started With a Hiring&nbsp;Problem.</span></h1>
            </Reveal>
            <Reveal now delay={0.16}>
              <p className="about-lead" style={{ maxWidth: 560 }}>
                In 2021, we started ConsultMeIn with a simple objective: help companies find the right people.
                What followed was a journey through startup hiring, leadership hiring, executive hiring and executive
                search — and hundreds of conversations with CHROs and Recruitment Heads. Those conversations revealed
                a problem that couldn't be ignored. The hiring process was fundamentally broken.
              </p>
            </Reveal>
            <Reveal now delay={0.24}>
              <div className="about-cta-row" style={{ marginTop: '2rem' }}>
                <button className="about-btn about-btn-primary" onClick={() => scrollTo('about-problem')} data-testid="about-hero-story-btn">Explore Our Story</button>
                <button className="about-btn about-btn-outline" onClick={() => scrollTo('about-chire')} data-testid="about-hero-discover-btn">Discover CHIRE</button>
                {TEAM_PAGE_LIVE && <button className="about-btn about-btn-outline" onClick={() => navigate('/about/team')} data-testid="about-hero-team-btn">Meet the Team</button>}
              </div>
            </Reveal>
          </div>
          <Reveal now delay={0.2}><HeroVisual /></Reveal>
        </div>
      </section>

      {/* ─── THE PROBLEM ─── */}
      <section id="about-problem" className="about-section" style={{ background: '#fff', borderTop: '1px solid #ECE7DE', borderBottom: '1px solid #ECE7DE' }}>
        <div className="about-container">
          <Reveal>
            <div className="about-narrow about-center" style={{ marginBottom: '2.75rem' }}>
              <div className="about-eyebrow">The problem</div>
              <h2 className="about-h2">The Problem Wasn't a Lack of Effort</h2>
              <p className="about-p" style={{ marginTop: '0.5rem' }}>
                Recruiters were working hard. Hiring managers were spending hours reviewing profiles and interviewing
                candidates. HR leaders were investing in processes and technology. Candidates were investing weeks of
                their time. Yet the problems remained.
              </p>
            </div>
          </Reveal>

          <RevealGroup className="about-prob-grid">
            {PROBLEMS.map((p) => (
              <Reveal key={p}><div className="about-prob"><span className="about-prob-dot" /><span>{p}</span></div></Reveal>
            ))}
          </RevealGroup>

          <Reveal>
            <div className="about-center" style={{ margin: 'clamp(3rem,6vw,4.5rem) auto 0' }}>
              <p className="about-pull about-oneline" style={{ fontSize: 'clamp(1.4rem, 3vw, 2.25rem)' }}>The resume wasn't actually a match for the job.</p>
            </div>
          </Reveal>

          <Reveal>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', margin: '2.5rem auto 0', maxWidth: 760, textAlign: 'center' }}>
              <div>
                <div className="about-stat-num" style={{ color: '#F05000' }}>3–4 <span style={{ fontSize: '0.4em' }}>weeks</span></div>
              </div>
              <p className="about-p" style={{ maxWidth: 420, textAlign: 'left', margin: 0 }}>
                That was sometimes how long it took to discover the mismatch — after screening, interviews and multiple
                stakeholder interactions.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="about-flow" style={{ maxWidth: 920, margin: '2.5rem auto 0' }}>
              <div className="about-flow-step"><strong>Resume</strong></div>
              {Arrow}
              <div className="about-flow-step"><strong>Screening</strong></div>
              {Arrow}
              <div className="about-flow-step"><strong>Interviews</strong></div>
              {Arrow}
              <div className="about-flow-step is-fail"><strong style={{ color: '#C2531F' }}>Discovery of mismatch</strong></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── THE CONVERSATIONS ─── */}
      <section className="about-section">
        <div className="about-container">
          <Reveal>
            <div className="about-narrow about-center" style={{ marginBottom: '2.5rem' }}>
              <div className="about-eyebrow">The conversations</div>
              <h2 className="about-h2">The Conversations That Changed Everything</h2>
            </div>
          </Reveal>

          <Reveal>
            <div className="about-stats" style={{ maxWidth: 900, margin: '0 auto 2.5rem' }}>
              <div className="about-stat"><div className="about-stat-num"><CountUp to={50} suffix="+" /></div><div className="about-stat-label">CHROs interviewed</div></div>
              <div className="about-stat"><div className="about-stat-num">100+</div><div className="about-stat-label">Recruitment Heads</div></div>
              <div className="about-stat"><div className="about-stat-num"><CountUp to={5} suffix="+" /></div><div className="about-stat-label">Years of recruitment experience</div></div>
            </div>
          </Reveal>

          <Reveal>
            <p className="about-p about-narrow" style={{ textAlign: 'center' }}>
              During our journey at ConsultMeIn, we had the opportunity to speak with and interview more than 50 CHROs
              and hundreds of Recruitment Heads across companies and industries. We talked about the Why's…. the How's….
              the What's. We talked about hiring Managers, Senior Managers, Directors, VPs, Leaders and Executives.
            </p>
          </Reveal>

          <RevealGroup className="about-narrow" style={{ marginTop: '2.5rem' }}>
            {[
              'How do we know this candidate really fits the role?',
              'How do we know the candidate can actually do what the resume says?',
              'How do we make interviews more consistent?',
              'How do we reduce subjectivity?',
              "How do we know we're making the right hiring decision?",
            ].map((q) => (<Reveal key={q}><div className="about-q">{q}</div></Reveal>))}
          </RevealGroup>

          <Reveal>
            <p className="about-pull about-center" style={{ margin: '2.5rem auto 0', maxWidth: 720, color: AZURE }}>
              How do we make hiring better?
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── THE PATTERN (bridge) ─── */}
      <section className="about-section" style={{ paddingTop: 0 }}>
        <div className="about-container about-narrow about-center">
          <Reveal>
            <div className="about-eyebrow">The pattern</div>
            <p className="about-p" style={{ fontSize: 'clamp(1.35rem, 2.8vw, 1.8rem)', fontWeight: 600, color: '#002A38', lineHeight: 1.5 }}>
              The same gaps surfaced in nearly every conversation. The tools existed to manage the process — but not
              to bring intelligence to the decisions inside it. That realization pointed to something that had to be
              built, one question at a time.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── EVOLUTION TIMELINE (centerpiece) ─── */}
      <section className="about-section" style={{ background: '#fff', borderTop: '1px solid #ECE7DE', borderBottom: '1px solid #ECE7DE' }}>
        <div className="about-container">
          <Reveal>
            <div className="about-narrow about-center" style={{ marginBottom: '3rem' }}>
              <div className="about-eyebrow">The evolution</div>
              <h2 className="about-h2">One Question at a Time</h2>
              <p className="about-p" style={{ marginTop: '0.5rem' }}>
                Each stage began with a single, honest question — and the answer became the next step.
              </p>
            </div>
          </Reveal>

          <RevealGroup className="about-evo" stagger={0.12}>
            {EVOLUTION.map((s) => (
              <Reveal key={s.name} className={`about-evo-stage${s.chire ? ' is-chire' : ''}`}>
                <div className="about-evo-node">{s.year}</div>
                <div className="about-evo-card">
                  <div className="about-evo-year">{s.tag}</div>
                  <div className="about-evo-name">{s.name}</div>
                  <p className="about-evo-q">{s.q}</p>
                  <p className="about-evo-desc">{s.desc}</p>
                  <div className="about-evo-chips">{s.chips.map((c) => <span key={c} className="about-chip">{c}</span>)}</div>
                </div>
              </Reveal>
            ))}
          </RevealGroup>

          <Reveal>
            <p className="about-center" style={{ marginTop: '2.75rem', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 700, fontSize: 'clamp(1.1rem,2.4vw,1.5rem)', color: '#002A38' }}>
              This is where the journey changed — from <span style={{ color: '#5B6B74' }}>recruitment technology</span> to <span style={{ color: AZURE }}>hiring intelligence</span>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── BIRTH OF CHIRE / WHAT CHIRE IS ─── */}
      <section id="about-chire" className="about-section">
        <div className="about-container">
          <Reveal>
            <div className="about-center" style={{ marginBottom: '2.5rem' }}>
              <h2 style={{ fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 800, fontSize: 'clamp(3rem,9vw,6rem)', letterSpacing: '-0.04em', lineHeight: 1, margin: 0, background: 'linear-gradient(135deg,#00A0E0,#F05000)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>CHIRE</h2>
              <p style={{ marginTop: '0.75rem', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#5B6B74' }}>ConsultMeIn Hiring Intelligence, Recruitment Engine</p>
            </div>
          </Reveal>

          <Reveal>
            <p className="about-pull about-center about-narrow" style={{ marginBottom: '1rem' }}>
              CHIRE is not another Applicant Tracking System.
            </p>
            <p className="about-p about-narrow about-center" style={{ marginBottom: '2.75rem' }}>
              An ATS helps organizations manage the hiring process. CHIRE is designed to bring intelligence to the
              decisions within that process.
            </p>
          </Reveal>

          <div className="about-compare">
            <Reveal className="about-compare-col">
              <div className="about-compare-col" style={{ padding: 0, border: 'none', background: 'transparent' }}>
                <div className="about-compare-kicker">Traditional ATS</div>
                <div className="about-compare-title" style={{ color: '#002A38' }}>Manages the process</div>
                <ul className="about-compare-list">
                  {['Manage applications', 'Track candidates', 'Manage workflow', 'Store recruitment information'].map((x) => (
                    <li key={x}><svg className="about-tick" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9DB0B9" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /></svg>{x}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal className="about-compare-col is-chire">
              <div className="about-compare-col is-chire" style={{ padding: 0, border: 'none', background: 'transparent' }}>
                <div className="about-compare-kicker">CHIRE</div>
                <div className="about-compare-title" style={{ color: '#fff' }}>Brings intelligence to decisions</div>
                <ul className="about-compare-list">
                  {['Understand role requirements', 'Match skills and experience', 'Assess capability', 'Structure interviews', 'Evaluate candidates', 'Verify information', 'Generate hiring intelligence', 'Build secure talent intelligence'].map((x) => (
                    <li key={x}><span style={{ color: '#8FD0E8' }}>{Tick}</span>{x}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── PHILOSOPHY ─── */}
      <section className="about-section" style={{ background: '#fff', borderTop: '1px solid #ECE7DE', borderBottom: '1px solid #ECE7DE' }}>
        <div className="about-container">
          <Reveal>
            <p className="about-quote about-narrow about-center" style={{ marginBottom: '3rem' }}>
              We believe hiring should be as human as the people it serves — and as rigorous as the data behind it.
            </p>
          </Reveal>
          <div className="about-phil">
            {[
              { tag: 'For employers', d: 'Decisions backed by real skill signals rather than guesswork.' },
              { tag: 'For candidates', d: "Being seen for what they can do, not just where they've been." },
              { tag: 'For employees', d: 'Growth paths that are visible, fair and earned.' },
            ].map((c) => (
              <Reveal key={c.tag}><div className="about-phil-col"><div className="about-phil-tag">{c.tag}</div><p className="about-p" style={{ fontSize: '1rem', margin: 0 }}>{c.d}</p></div></Reveal>
            ))}
          </div>
          <Reveal>
            <p className="about-pull about-center about-narrow" style={{ margin: '3rem auto 1.75rem' }}>
              Technology should sharpen judgment, never replace the care that ideal hiring needs.
            </p>
          </Reveal>
          <Reveal>
            <div className="about-word-flow">
              <span>Human</span><span className="sep">→</span><span>Data</span><span className="sep">→</span>
              <span>Judgment</span><span className="sep">→</span><span>Care</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── BEYOND HIRING ─── */}
      <section className="about-section">
        <div className="about-container">
          <Reveal>
            <div className="about-narrow about-center" style={{ marginBottom: '2.5rem' }}>
              <div className="about-eyebrow">Beyond hiring</div>
              <h2 className="about-h2">Beyond Hiring</h2>
              <p className="about-p" style={{ marginTop: '0.5rem' }}>
                We believe the future of HR will not be built on dozens of disconnected systems that each know one
                small piece of the employee journey. It will be built on connected intelligence.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', fontSize: '0.8rem', fontWeight: 600 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: AZURE }}><span style={{ width: 10, height: 10, borderRadius: 3, background: '#E7F2F7', border: '1px solid #CDE6EF' }} /> Available today</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#C2531F' }}><span style={{ width: 10, height: 10, borderRadius: 3, background: '#FFF6F2', border: '1px dashed #F6C9BB' }} /> Where we're going</span>
            </div>
          </Reveal>

          <Reveal>
            <div className="about-lifecycle">
              {LIFECYCLE.map((s, i) => (
                <React.Fragment key={s.t}>
                  <span className={`about-life-stage ${s.today ? 'is-today' : 'is-future'}`}>{s.t}</span>
                  {i < LIFECYCLE.length - 1 && <svg className="about-life-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>}
                </React.Fragment>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <h3 className="about-h3 about-center" style={{ margin: '3rem 0 1.25rem', fontSize: '0.8rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C2531F' }}>Where we're going next</h3>
            <div className="about-future-grid about-narrow">
              {FUTURE_AREAS.map((f) => <div key={f} className="about-future-item">{f}</div>)}
            </div>
          </Reveal>

          <Reveal>
            <p className="about-pull about-center about-narrow" style={{ margin: '3rem auto 0' }}>
              From hiring intelligence to human capital intelligence.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── WHAT WE ARE BUILDING ─── */}
      <section className="about-section" style={{ background: '#fff', borderTop: '1px solid #ECE7DE', borderBottom: '1px solid #ECE7DE' }}>
        <div className="about-container">
          <Reveal>
            <div className="about-narrow about-center" style={{ marginBottom: '2.75rem' }}>
              <div className="about-eyebrow">The platform</div>
              <h2 className="about-h2">What We Are Building</h2>
            </div>
          </Reveal>
          <RevealGroup className="about-cap-grid" stagger={0.06}>
            {CAPABILITIES.map((c) => (
              <Reveal key={c.t}>
                <div className="about-cap">
                  <div className="about-cap-icon">{c.icon}</div>
                  <h3 className="about-h3">{c.t}</h3>
                  <p className="about-p" style={{ fontSize: '0.9375rem', margin: 0 }}>{c.d}</p>
                </div>
              </Reveal>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* ─── TRUST FRAMEWORK ─── */}
      <section className="about-section about-dark">
        <div className="grain-overlay" style={{ opacity: 0.18 }} />
        <div className="about-container" style={{ position: 'relative', zIndex: 1 }}>
          <Reveal>
            <div className="about-narrow about-center" style={{ marginBottom: '2.75rem' }}>
              <div className="about-eyebrow">Trust framework</div>
              <h2 className="about-h2">Trust Is Not a Feature. It Is the Foundation.</h2>
              <p className="about-p" style={{ color: 'rgba(255,255,255,0.72)', marginTop: '0.6rem' }}>
                When technology influences decisions about people, trust cannot be an afterthought.
              </p>
            </div>
          </Reveal>
          <RevealGroup className="about-trust-grid" stagger={0.05}>
            {TRUST.map((t, i) => (
              <Reveal key={t.t}>
                <div className="about-trust">
                  <div className="about-trust-num">{String(i + 1).padStart(2, '0')}</div>
                  <div className="about-trust-title">{t.t}</div>
                  <p className="about-trust-desc">{t.d}</p>
                </div>
              </Reveal>
            ))}
          </RevealGroup>
          <Reveal>
            <div className="about-narrow about-center" style={{ marginTop: '3rem' }}>
              <p className="about-quote about-quote-dark">"If this technology were making a decision about us, would we trust it?"</p>
              <p className="about-p" style={{ color: 'rgba(255,255,255,0.72)', marginTop: '1rem' }}>If the answer is no, we have more work to do.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── FOUNDER JOURNEY ─── */}
      <section className="about-section">
        <div className="about-container">
          <Reveal>
            <div className="about-narrow about-center" style={{ marginBottom: '2.5rem' }}>
              <div className="about-eyebrow">The journey</div>
              <h2 className="about-h2">Built From Real Recruitment Experience</h2>
              <p className="about-p" style={{ marginTop: '0.6rem' }}>
                CHIRE wasn't created because we thought HR needed another piece of software. It was created because we
                spent years doing the work. We saw the problems. We heard the frustrations. We experienced the
                inefficiencies. We spoke to the people responsible for hiring. And we saw what happened when the wrong
                decision was made.
              </p>
            </div>
          </Reveal>

          <RevealGroup className="about-journey" stagger={0.05}>
            {JOURNEY.map((j) => (
              <Reveal key={j.t} className={`about-journey-item${j.chire ? ' is-chire' : ''}`}>
                <h4>{j.t}</h4>{j.y && <p>{j.y}</p>}
              </Reveal>
            ))}
          </RevealGroup>

          <Reveal>
            <blockquote className="about-narrow about-center" style={{ margin: '3rem auto 0', padding: 0 }}>
              <p className="about-quote" style={{ color: '#002A38' }}>
                "We are proud that what started as a recruitment company in 2021 has evolved into an attempt to
                fundamentally change how organizations understand, assess, hire and develop people."
              </p>
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* ─── THE FUTURE ─── */}
      <section className="about-section" style={{ background: '#fff', borderTop: '1px solid #ECE7DE', borderBottom: '1px solid #ECE7DE' }}>
        <div className="about-container about-narrow about-center">
          <Reveal>
            <div className="about-eyebrow">The future we see</div>
            <h2 className="about-h2">What if HR could understand people with the same intelligence modern technology brings to everything else?</h2>
          </Reveal>
          <Reveal>
            <p className="about-p" style={{ marginTop: '1.25rem', textAlign: 'left' }}>
              We see a future where HR doesn't have to work with fragmented information. Where recruiters don't have to
              spend weeks discovering that a candidate was never really a match. Where hiring managers have better
              evidence behind their decisions. Where candidates are evaluated for what they can actually do. Where
              skills become more visible. Where assessments become more meaningful. Where interviews become more
              consistent. Where AI handles intelligence at scale while humans retain judgment, empathy and
              accountability. And where the intelligence created during hiring can continue creating value throughout a
              person's journey inside an organization.
            </p>
          </Reveal>
          <Reveal>
            <p className="about-pull" style={{ marginTop: '2.5rem' }}>
              Our ambition is simple.<br />
              <span style={{ color: AZURE }}>To build the platform One can trust for all things related to Human Resources.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {TEAM_PAGE_LIVE && <LeadershipPreview eyebrow="Meet the leadership" title="The leaders building CHIRE" testid="about-leadership" />}

      {/* ─── FINAL CTA ─── */}
      <section className="about-section about-dark">
        <div className="grain-overlay" style={{ opacity: 0.22 }} />
        <div className="about-container about-narrow about-center" style={{ position: 'relative', zIndex: 1 }}>
          <Reveal>
            <h2 className="about-h2">The future of hiring needs more than another system.</h2>
            <p className="about-lead" style={{ color: 'rgba(255,255,255,0.75)', marginTop: '0.75rem' }}>It needs Intelligence you can trust.</p>
          </Reveal>
          <Reveal>
            <div className="about-cta-row" style={{ justifyContent: 'center', marginTop: '2rem' }}>
              <button className="about-btn about-btn-primary" onClick={() => navigate('/')} data-testid="about-cta-explore-btn">Explore CHIRE</button>
              <button className="about-btn about-btn-ghost-dark" onClick={() => navigate('/connectwithus')} data-testid="about-cta-talk-btn">Talk to Us</button>
            </div>
          </Reveal>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
